import React, { useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { headerSettings } from "./utils/tableSettings";
import EmptySummary from "./utils/EmptySummary";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../utils/export/componentExporter";

const CaSummary = ({ caType, dataRows }) => {
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, `${caType} Summary`);
    }
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const includeIA = (
    headerSettings.find((h) => h.key === "initialAllowance")?.visibility || []
  ).includes(caType);

  // Group dataRows by caSummary
  const groupedData = {};
  dataRows.forEach((row) => {
    if (!groupedData[row.caSummary]) {
      groupedData[row.caSummary] = [];
    }
    groupedData[row.caSummary].push(row);
  });
  const hasMultipleSummaryTypes = Object.keys(groupedData).length > 1;
  const isSingleSummaryRow = dataRows.length === 1;

  // Calculate totals
  const caSummaryTotals = {};
  Object.keys(groupedData).forEach((summary) => {
    caSummaryTotals[summary] = {
      totalIA: groupedData[summary].reduce(
        (acc, row) => acc + (row.totalIA || 0),
        0
      ),
      totalAA: groupedData[summary].reduce(
        (acc, row) => acc + (row.totalAA || 0),
        0
      ),
    };
  });
  const grandTotalIA = dataRows.reduce(
    (acc, row) => acc + (row.totalIA || 0),
    0
  );
  const grandTotalAA = dataRows.reduce(
    (acc, row) => acc + (row.totalAA || 0),
    0
  );

  if (!dataRows || dataRows.length === 0) {
    return <EmptySummary caType={caType} />;
  }

  return (
    <Paper
      ref={exportRef}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.618),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant='h6' fontWeight={600} color='primary.main'>
            CA Summary
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {includeIA
              ? "Initial Allowance and Annual Allowance"
              : "Annual Allowance"}{" "}
            summary based on the <b>CA Summary Type</b>
          </Typography>
        </Box>
        <CameraExportButton
          onClick={handleExportAsImage}
          tooltipTitle='Screenshot of the CA Summary'
          sx={{ mr: 4 }}
        />
      </Box>
      {/* Summary Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                CA Summary
              </TableCell>
              {includeIA && (
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: 600,
                    fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    width: "200px",
                  }}
                >
                  Initial Allowance
                </TableCell>
              )}
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                  width: "200px",
                }}
              >
                Annual Allowance
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(groupedData).map((summary, summaryIdx) => {
              const rows = groupedData[summary];
              const totals = caSummaryTotals[summary];
              return (
                <React.Fragment key={summary}>
                  {/* Individual rows for this summary */}
                  {rows.map((row, rowIdx) => (
                    <TableRow
                      key={`${summary}-${rowIdx}`}
                      sx={{
                        "&:hover": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: rowIdx === 0 ? 600 : 400,
                          color: rowIdx === 0 ? "primary.main" : "text.primary",
                          pl: rowIdx === 0 ? 3 : 6,
                        }}
                      >
                        {rowIdx === 0 ? summary : ""}
                      </TableCell>
                      {includeIA && (
                        <TableCell
                          align='right'
                          sx={isSingleSummaryRow ? { fontWeight: 700 } : undefined}
                        >
                          {formatAmount(row.totalIA)}
                        </TableCell>
                      )}
                      <TableCell
                        align='right'
                        sx={isSingleSummaryRow ? { fontWeight: 700 } : undefined}
                      >
                        {formatAmount(row.totalAA)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* CA Summary Total Row */}
                  {hasMultipleSummaryTypes && (
                    <TableRow
                      sx={{
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.04),
                        borderTop: "2px solid",
                        borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                      }}
                    >
                      <TableCell
                        sx={{ fontWeight: 700, color: "primary.main", pl: 3 }}
                      >
                        {summary} Total
                      </TableCell>
                      {includeIA && (
                        <TableCell
                          align='right'
                          sx={{ fontWeight: 700, color: "primary.main" }}
                        >
                          {formatAmount(totals.totalIA)}
                        </TableCell>
                      )}
                      <TableCell
                        align='right'
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        {formatAmount(totals.totalAA)}
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Spacer row between summaries */}
                  {summaryIdx < Object.keys(groupedData).length - 1 && (
                    <TableRow>
                      <TableCell
                        colSpan={includeIA ? 3 : 2}
                        sx={{ height: 16 }}
                      ></TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {/* Grand Total Row */}
            {!isSingleSummaryRow && (
              <TableRow
                sx={{
                  borderTop: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "primary.main",
                    pl: 3,
                  }}
                >
                  {hasMultipleSummaryTypes ? "Grand Total" : "Total"}
                </TableCell>
                {includeIA && (
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "primary.main",
                    }}
                  >
                    {formatAmount(grandTotalIA)}
                  </TableCell>
                )}
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "primary.main",
                  }}
                >
                  {formatAmount(grandTotalAA)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CaSummary;
